import { useEffect, useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { api, ApiError, errorText } from '../../api';
import { useToast } from '../../lib/toast';
import { EU_LAENDER } from '../laender';
import { istDrittland, LAENDER_WELT } from '../laender-welt';
import { fehlerText } from '../tarifHinweis';
import type { Overview } from '../types';
import { lieferantenUrl, lieferantUrl, type Lieferant, type LieferantAntwort } from './LieferantDaten';
import { SchlagwortFeld } from './Schlagwoerter';
import '../kunden.css';
import './kontakte.css';

interface Werte {
  name: string;
  extra: string;
  street: string;
  zip: string;
  city: string;
  country: string;
  vatId: string;
  email: string;
  phone: string;
  iban: string;
  kundennummerDort: string;
  notiz: string;
  schlagwoerter: string[];
}

const LEER: Werte = {
  name: '',
  extra: '',
  street: '',
  zip: '',
  city: '',
  country: 'DE',
  vatId: '',
  email: '',
  phone: '',
  iban: '',
  kundennummerDort: '',
  notiz: '',
  schlagwoerter: [],
};

const werteVon = (l: Lieferant): Werte => ({
  name: l.name,
  extra: l.extra ?? '',
  street: l.street,
  zip: l.zip,
  city: l.city,
  country: l.country,
  vatId: l.vatId ?? '',
  email: l.email ?? '',
  phone: l.phone ?? '',
  iban: l.iban ?? '',
  kundennummerDort: l.kundennummerDort ?? '',
  notiz: l.notiz ?? '',
  schlagwoerter: l.schlagwoerter,
});

// Länder: Deutschland, die EU, dann die übrige Welt (wie beim Kunden)
const LAENDER: [string, string][] = [['DE', 'Deutschland'], ...EU_LAENDER.map(([c, n]) => [c, n] as [string, string]), ...LAENDER_WELT];

// Neuer Lieferant (/rechnung/lieferanten/neu) oder Lieferant bearbeiten (/:id/bearbeiten). Name Pflicht, Anschrift
// frei; die Nummer vergibt der Server. Prüfen macht der Server (PLZ, USt-IdNr., IBAN); derselbe Name (mit derselben
// PLZ): erst ein Hinweis, dann auf Wunsch trotzdem.
export function LieferantForm({
  data,
  id,
  vorschlaege,
  onSaved,
}: {
  data: Overview;
  id?: string;
  vorschlaege: string[];
  onSaved(): Promise<void>;
}) {
  const toast = useToast();
  const navigate = useNavigate();
  const [w, setW] = useState<Werte | null>(id ? null : LEER);
  const [ladeFehler, setLadeFehler] = useState<string | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [doppelt, setDoppelt] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!id) return;
    let weg = false;
    api
      .get<LieferantAntwort>(lieferantUrl(id))
      .then((r) => !weg && setW(werteVon(r.lieferant)))
      .catch((err) => !weg && setLadeFehler(errorText(err)));
    return () => {
      weg = true;
    };
  }, [id]);

  if (ladeFehler) {
    return (
      <div className="notice bad">
        {ladeFehler} <Link to="/rechnung/lieferanten">Zur Lieferantenliste</Link>
      </div>
    );
  }
  if (!w) return <div className="spinner" aria-label="Lädt" />;
  const welt = istDrittland(w.country);
  const set = (v: Partial<Werte>) => {
    setW({ ...w, ...v });
    setDoppelt(null);
  };

  async function save(trotzdem = false) {
    if (!w) return;
    setBusy(true);
    setFehler(null);
    setDoppelt(null);
    try {
      const body = { ...w, trotzdem };
      const res = id
        ? await api.put<{ id: string; nummer: number }>(lieferantUrl(id), body)
        : await api.post<{ id: string; nummer: number }>(lieferantenUrl(data.firm.id), body);
      await onSaved();
      toast(id ? 'Lieferant gespeichert' : `Lieferant ${res.nummer} angelegt`);
      navigate(`/rechnung/lieferanten/${res.id}`, { replace: !!id });
    } catch (err) {
      if (err instanceof ApiError && err.code === 'doppelt') setDoppelt(err.message);
      else setFehler(fehlerText(err));
    } finally {
      setBusy(false);
    }
  }

  function submit(e: FormEvent) {
    e.preventDefault();
    void save();
  }

  const zurueck = id ? `/rechnung/lieferanten/${id}` : '/rechnung/lieferanten';
  return (
    <form className="rt-card kd-form" onSubmit={submit} noValidate>
      <Link to={zurueck} className="kd-back">
        ← {id ? 'Zurück zum Lieferanten' : 'Alle Lieferanten'}
      </Link>
      <h1>{id ? 'Lieferant bearbeiten' : 'Neuer Lieferant'}</h1>
      <div className="rt-grid">
        <label className="field rt-span2">
          <span>Name</span>
          <input value={w.name} maxLength={120} autoComplete="off" onChange={(e) => set({ name: e.target.value })} />
        </label>
        <label className="field rt-span2">
          <span>Zusatz (optional)</span>
          <input value={w.extra} maxLength={120} placeholder="z. B. Abteilung Vertrieb" onChange={(e) => set({ extra: e.target.value })} />
        </label>
        <label className="field rt-span2">
          <span>Straße (optional)</span>
          <input value={w.street} maxLength={120} onChange={(e) => set({ street: e.target.value })} />
        </label>
        <label className="field">
          <span>PLZ (optional)</span>
          <input
            value={w.zip}
            maxLength={12}
            inputMode={w.country === 'DE' ? 'numeric' : 'text'}
            onChange={(e) => set({ zip: e.target.value })}
          />
        </label>
        <label className="field">
          <span>Ort (optional)</span>
          <input value={w.city} maxLength={120} onChange={(e) => set({ city: e.target.value })} />
        </label>
        <label className="field rt-span2">
          <span>Land</span>
          <select value={w.country} onChange={(e) => set({ country: e.target.value })}>
            {LAENDER.map(([code, name]) => (
              <option key={code} value={code}>
                {name}
              </option>
            ))}
          </select>
        </label>
        <label className="field rt-span2">
          <span>{welt ? 'Steuernummer (optional)' : 'USt-IdNr. (optional)'}</span>
          <input
            value={w.vatId}
            maxLength={20}
            autoComplete="off"
            placeholder={welt ? undefined : 'z. B. DE123456789'}
            onChange={(e) => set({ vatId: e.target.value })}
          />
        </label>
      </div>

      <h2>Kontakt</h2>
      <div className="rt-grid">
        <label className="field rt-span2">
          <span>E-Mail (optional)</span>
          <input type="email" value={w.email} maxLength={120} autoComplete="off" onChange={(e) => set({ email: e.target.value })} />
        </label>
        <label className="field rt-span2">
          <span>Telefon (optional)</span>
          <input type="tel" value={w.phone} maxLength={40} autoComplete="off" onChange={(e) => set({ phone: e.target.value })} />
        </label>
        <label className="field rt-span2">
          <span>IBAN (optional)</span>
          <input value={w.iban} maxLength={60} autoComplete="off" spellCheck={false} onChange={(e) => set({ iban: e.target.value })} />
        </label>
        <label className="field rt-span2">
          <span>Unsere Kundennummer dort (optional)</span>
          <input value={w.kundennummerDort} maxLength={40} autoComplete="off" onChange={(e) => set({ kundennummerDort: e.target.value })} />
        </label>
      </div>
      <SchlagwortFeld value={w.schlagwoerter} vorschlaege={vorschlaege} onChange={(v) => set({ schlagwoerter: v })} />
      <label className="field">
        <span>Notiz (optional)</span>
        <textarea
          value={w.notiz}
          maxLength={1000}
          placeholder="z. B. Bestellungen immer per E-Mail"
          onChange={(e) => set({ notiz: e.target.value })}
        />
      </label>

      {fehler && <div className="notice bad">{fehler}</div>}
      {doppelt && (
        <div className="notice warn kd-doppelt">
          <span>{doppelt}</span>
          <button type="button" className="btn small" disabled={busy} onClick={() => void save(true)}>
            Trotzdem speichern
          </button>
        </div>
      )}
      <div className="rt-actions">
        <Link to={zurueck} className="btn">
          Abbrechen
        </Link>
        <button type="submit" className="btn primary" disabled={busy}>
          {busy ? 'Wird gespeichert …' : id ? 'Speichern' : 'Lieferant anlegen'}
        </button>
      </div>
    </form>
  );
}
